import { useState, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { 
  Calendar as CalendarIcon, 
  Plus, 
  Pencil, 
  Trash2, 
  X, 
  Save,
  Sparkles,
  Users,
  ArrowLeft
} from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import axios from "axios";
import logger from "@/lib/logger";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const eventTypes = [
  { value: "workshop", label: "Workshop", icon: Sparkles, color: "bg-soar-teal" },
  { value: "program", label: "Program", icon: Users, color: "bg-[#212163]" },
  { value: "deadline", label: "Deadline", icon: CalendarIcon, color: "bg-soar-gold" },
];

const emptyEvent = {
  title: "",
  event_type: "program",
  description: "",
  date: "",
  start_time: "",
  end_time: "",
  location: "",
  registration_open: true,
  is_deadline: false,
};

const AdminCalendar = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [formData, setFormData] = useState(emptyEvent);
  const [saving, setSaving] = useState(false);

  const fetchEvents = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/calendar`);
      const sortedEvents = response.data.sort((a, b) => 
        new Date(a.date) - new Date(b.date)
      );
      setEvents(sortedEvents);
    } catch (error) {
      logger.error("Failed to fetch events:", error);
      toast.error("Failed to load events");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title || !formData.date) {
      toast.error("Title and date are required");
      return;
    }

    setSaving(true);

    try {
      if (editingEvent) {
        await axios.put(`${API}/calendar/${editingEvent.id}`, formData);
        toast.success("Event updated successfully");
      } else {
        await axios.post(`${API}/calendar`, formData);
        toast.success("Event created successfully");
      }
      
      setShowForm(false);
      setEditingEvent(null);
      setFormData(emptyEvent);
      fetchEvents();
    } catch (error) {
      logger.error("Failed to save event:", error);
      toast.error("Failed to save event");
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (event) => {
    setEditingEvent(event);
    setFormData({
      title: event.title,
      event_type: event.event_type,
      description: event.description || "",
      date: event.date,
      start_time: event.start_time || "",
      end_time: event.end_time || "",
      location: event.location || "",
      registration_open: event.registration_open ?? true,
      is_deadline: event.is_deadline ?? false,
    });
    setShowForm(true);
  };

  const handleDelete = async (eventId, eventTitle) => {
    if (!window.confirm(`Are you sure you want to delete "${eventTitle}"?`)) {
      return;
    }

    try {
      await axios.delete(`${API}/calendar/${eventId}`);
      toast.success("Event deleted successfully");
      fetchEvents();
    } catch (error) {
      logger.error("Failed to delete event:", error);
      toast.error("Failed to delete event");
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingEvent(null);
    setFormData(emptyEvent);
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr + 'T00:00:00');
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric' 
    });
  };

  const getEventTypeConfig = (type) => {
    return eventTypes.find(t => t.value === type) || eventTypes[1];
  };

  return (
    <div data-testid="admin-calendar-page" className="bg-white min-h-screen">
      {/* Header */}
      <section className="py-12 bg-soar-blue">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-center gap-4 mb-4">
            <Link to="/calendar" className="text-white/60 hover:text-white transition-colors">
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <span className="text-soar-gold text-xs font-semibold tracking-widest uppercase">Admin Panel</span>
          </div>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <h1 className="font-heading text-4xl sm:text-5xl text-white tracking-tight">
              Manage <span className="text-soar-gold">Calendar</span>
            </h1>
            <Button
              onClick={() => setShowForm(true)}
              className="rounded-full bg-soar-gold hover:bg-soar-gold/90 text-soar-blue font-bold px-6"
              data-testid="add-event-btn"
            >
              <Plus className="w-4 h-4 mr-2" /> Add Event
            </Button>
          </div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-6 py-12">
        {/* Form Modal */}
        {showForm && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-3xl max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl">
              <div className="p-6 border-b border-soar-blue/10 flex items-center justify-between">
                <h2 className="font-heading text-2xl text-soar-blue">
                  {editingEvent ? "Edit Event" : "Add New Event"}
                </h2>
                <button
                  onClick={handleCancel}
                  className="p-2 rounded-full hover:bg-soar-blue/5 text-soar-blue/50 hover:text-soar-blue transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              
              <form onSubmit={handleSubmit} className="p-6 space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="title" className="text-soar-blue">Event Title *</Label>
                  <Input
                    id="title"
                    name="title"
                    value={formData.title}
                    onChange={handleInputChange}
                    placeholder="e.g., Youth Job Fair"
                    className="rounded-xl border-soar-blue/20 focus:border-soar-teal"
                    data-testid="event-title-input"
                    required
                  />
                </div>

                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="event_type" className="text-soar-blue">Event Type</Label>
                    <select
                      id="event_type"
                      name="event_type"
                      value={formData.event_type}
                      onChange={handleInputChange}
                      className="w-full rounded-xl border border-soar-blue/20 bg-white px-4 py-2.5 text-soar-blue focus:border-soar-teal focus:outline-none focus:ring-2 focus:ring-soar-teal/20"
                      data-testid="event-type-select"
                    >
                      {eventTypes.map(type => (
                        <option key={type.value} value={type.value}>{type.label}</option>
                      ))}
                    </select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="date" className="text-soar-blue">Date *</Label>
                    <Input
                      id="date"
                      name="date"
                      type="date"
                      value={formData.date}
                      onChange={handleInputChange}
                      className="rounded-xl border-soar-blue/20 focus:border-soar-teal"
                      data-testid="event-date-input"
                      required
                    />
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="start_time" className="text-soar-blue">Start Time</Label>
                    <Input
                      id="start_time"
                      name="start_time"
                      placeholder="e.g., 10:00 AM or TBD"
                      value={formData.start_time}
                      onChange={handleInputChange}
                      className="rounded-xl border-soar-blue/20 focus:border-soar-teal"
                      data-testid="event-start-time-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="end_time" className="text-soar-blue">End Time</Label>
                    <Input
                      id="end_time"
                      name="end_time"
                      placeholder="e.g., 2:00 PM or TBD"
                      value={formData.end_time}
                      onChange={handleInputChange}
                      className="rounded-xl border-soar-blue/20 focus:border-soar-teal"
                      data-testid="event-end-time-input"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="location" className="text-soar-blue">Location</Label>
                  <Input
                    id="location"
                    name="location"
                    placeholder="e.g., SOAR Youth Center or TBD"
                    value={formData.location}
                    onChange={handleInputChange}
                    className="rounded-xl border-soar-blue/20 focus:border-soar-teal"
                    data-testid="event-location-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="description" className="text-soar-blue">Description</Label>
                  <Textarea
                    id="description"
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    placeholder="Describe the event..."
                    rows={4}
                    className="rounded-xl border-soar-blue/20 focus:border-soar-teal resize-none"
                    data-testid="event-description-input"
                  />
                </div>

                <div className="flex flex-wrap gap-6">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      name="registration_open"
                      checked={formData.registration_open}
                      onChange={handleInputChange}
                      className="w-4 h-4 rounded border-soar-blue/30 text-soar-teal focus:ring-soar-teal"
                    />
                    <span className="text-soar-blue text-sm">Registration Open</span>
                  </label>

                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      name="is_deadline"
                      checked={formData.is_deadline}
                      onChange={handleInputChange}
                      className="w-4 h-4 rounded border-soar-blue/30 text-soar-teal focus:ring-soar-teal"
                    />
                    <span className="text-soar-blue text-sm">Is Deadline</span>
                  </label>
                </div>

                <div className="flex gap-3 pt-4">
                  <Button
                    type="button"
                    onClick={handleCancel}
                    className="flex-1 rounded-full bg-soar-blue/10 hover:bg-soar-blue/20 text-soar-blue font-bold"
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    disabled={saving}
                    className="flex-1 rounded-full bg-soar-teal hover:bg-soar-teal/90 text-white font-bold"
                    data-testid="save-event-btn"
                  >
                    {saving ? (
                      <span className="flex items-center gap-2">
                        <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        Saving...
                      </span>
                    ) : (
                      <span className="flex items-center gap-2">
                        <Save className="w-4 h-4" />
                        {editingEvent ? "Update Event" : "Create Event"}
                      </span>
                    )}
                  </Button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Events List */}
        {loading ? (
          <div className="flex justify-center py-20">
            <div className="animate-spin w-8 h-8 border-2 border-soar-teal border-t-transparent rounded-full"></div>
          </div>
        ) : events.length === 0 ? (
          <div className="text-center py-20">
            <CalendarIcon className="w-16 h-16 text-soar-blue/20 mx-auto mb-4" />
            <h3 className="font-heading text-2xl text-soar-blue mb-2">No Events Yet</h3>
            <p className="text-soar-blue/50 mb-6">Create your first event to get started.</p>
            <Button
              onClick={() => setShowForm(true)}
              className="rounded-full bg-soar-teal hover:bg-soar-teal/90 text-white font-bold px-8"
            >
              <Plus className="w-4 h-4 mr-2" /> Add Event
            </Button>
          </div>
        ) : (
          <div className="space-y-4" data-testid="events-list">
            {events.map((event) => {
              const typeConfig = getEventTypeConfig(event.event_type);
              const Icon = typeConfig.icon;
              
              return (
                <div
                  key={event.id}
                  className="card-elevated rounded-2xl p-4 sm:p-6 flex flex-col sm:flex-row sm:items-center gap-4"
                  data-testid={`event-item-${event.id}`}
                >
                  <div className={`${typeConfig.color} w-14 h-14 rounded-xl flex items-center justify-center flex-shrink-0`}>
                    <Icon className="w-6 h-6 text-white" />
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full uppercase tracking-wider ${
                        event.event_type === 'workshop' ? 'bg-soar-teal/20 text-soar-teal' :
                        event.event_type === 'deadline' ? 'bg-soar-gold/20 text-soar-gold' :
                        'bg-soar-blue/10 text-soar-blue'
                      }`}>
                        {typeConfig.label}
                      </span>
                      {!event.registration_open && (
                        <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-red-100 text-red-600 uppercase">
                          Closed
                        </span>
                      )}
                    </div>
                    <h3 className="font-heading text-lg text-soar-blue truncate">{event.title}</h3>
                    <p className="text-soar-blue/50 text-sm">
                      {formatDate(event.date)}
                      {event.start_time && event.start_time !== "TBD" && ` • ${event.start_time}`}
                      {event.location && event.location !== "TBD" && ` • ${event.location}`}
                    </p>
                  </div>
                  
                  <div className="flex gap-2 sm:flex-shrink-0">
                    <Button
                      onClick={() => handleEdit(event)}
                      className="rounded-full bg-soar-blue/10 hover:bg-soar-blue/20 text-soar-blue p-2.5"
                      data-testid={`edit-event-${event.id}`}
                    >
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button
                      onClick={() => handleDelete(event.id, event.title)}
                      className="rounded-full bg-red-50 hover:bg-red-100 text-red-600 p-2.5"
                      data-testid={`delete-event-${event.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminCalendar;
